import type { ReplyMessage } from "@go-novus/admin-api";
import type {
  WidgetCompareCard,
  WidgetProduct,
  WidgetSizeCard,
} from "../ui/message";

/** 终态回复里可以挂的东西。Socket 回复帧和历史接口补回的回答都是这个形状。 */
export type ReplyAttachments = Partial<
  Pick<
    ReplyMessage,
    | "reply_states"
    | "compare_card"
    | "grounding"
    | "product_cards"
    | "size_card"
  >
>;

/** 来源标注由 AI 后端生成，挂件原样展示，不从内部来源类型反推文案。 */
export function sourceLabel(reply: ReplyAttachments): string | undefined {
  return reply.grounding?.label;
}

/** 商品卡：图、名、价、链接。链接是店面商品页，点了就是普通跳转。 */
export function productCards(
  reply: ReplyAttachments,
  locale?: string,
): WidgetProduct[] | undefined {
  const cards = reply.product_cards ?? [];
  if (cards.length === 0) return undefined;
  return cards.map((card) => ({
    imageUrl: card.image_url ?? undefined,
    itemId: card.item_id,
    parentItemId: card.parent_item_id ?? undefined,
    price: formatPrice(card.price, card.currency, locale),
    title: card.title,
    url: card.url,
    variantId: card.variant_id ?? undefined,
  }));
}

/** 尺码卡：推荐码、美码、三围、来源都是后端原文，挂件只换成自己的字段名。 */
export function sizeCard(reply: ReplyAttachments): WidgetSizeCard | undefined {
  const card = reply.size_card;
  if (!card) return undefined;
  return {
    measurements: card.measurements.map(({ label, value }) => ({
      label,
      value,
    })),
    recommendedSize: card.recommended_size,
    source: card.source ?? undefined,
    usSize: card.us_size ?? undefined,
  };
}

/** 对比卡：两列标题和逐行的两个值；哪行不同在 resolveMessage 里判，快照里只存原文。 */
export function compareCard(
  reply: ReplyAttachments,
): WidgetCompareCard | undefined {
  const card = reply.compare_card;
  if (!card) return undefined;
  const column = ({ title, url }: (typeof card.columns)[number]) => ({
    title,
    url: url ?? undefined,
  });
  return {
    columns: [column(card.columns[0]), column(card.columns[1])],
    rows: card.rows.map(({ label, values }) => ({
      label,
      values: [values[0], values[1]],
    })),
  };
}

/** 后端给的是纯数字加币种码；币种码不可用时退回原样拼接，不吞掉价格。 */
export function formatPrice(
  price: string | null | undefined,
  currency: string | null | undefined,
  locale: string | undefined,
): string {
  if (!price) return "";
  if (!currency) return price;
  const amount = Number(price);
  if (!Number.isFinite(amount)) return `${currency} ${price}`;
  try {
    return new Intl.NumberFormat(locale, {
      currency,
      style: "currency",
    }).format(amount);
  } catch {
    return `${currency} ${price}`;
  }
}
